import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, PhoneCall } from "lucide-react";
import SITE from "@/lib/site";
import Logo from "@/components/site/Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FBFBF9]/90 backdrop-blur-xl border-b border-[#D5DDD7]/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 sm:h-20 flex items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {SITE.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? "text-[#1A3A32] bg-[#EDF1ED]"
                      : "text-[#495D56] hover:text-[#14241F] hover:bg-[#EDF1ED]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${SITE.legal.emergencyLine}`}
              data-testid="nav-emergency-call"
              className="inline-flex items-center gap-2 text-[#14241F] text-sm font-medium hover:text-[#E5383B] transition-colors"
            >
              <PhoneCall className="h-4 w-4" /> {SITE.legal.emergencyLine}
            </a>
            <Link
              to="/contact"
              data-testid="nav-book-button"
              className="inline-flex items-center justify-center bg-[#E5383B] text-white hover:bg-[#BA1826] active:bg-[#A4161A] rounded-lg px-5 py-2.5 font-semibold text-sm transition-colors shadow-sm shadow-red-500/20"
            >
              Book Ambulance
            </Link>
          </div>

          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md text-[#14241F] hover:bg-[#EDF1ED] transition-colors"
            aria-label="Toggle navigation"
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 sm:px-6 pb-6 pt-2 bg-[#FBFBF9] border-b border-[#D5DDD7]">
          <div className="flex flex-col gap-1">
            {SITE.nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-base font-medium ${
                    isActive
                      ? "bg-[#EDF1ED] text-[#1A3A32]"
                      : "text-[#14241F] hover:bg-[#EDF1ED]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <a
              href={`tel:${SITE.legal.emergencyLine}`}
              data-testid="nav-mobile-emergency-call"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#1A3A32] text-[#1A3A32] hover:bg-[#EDF1ED] rounded-lg px-6 py-3 font-semibold"
            >
              <PhoneCall className="h-4 w-4" /> Call {SITE.legal.emergencyLine}
            </a>
            <Link
              to="/contact"
              data-testid="nav-mobile-book-button"
              className="inline-flex items-center justify-center bg-[#E5383B] text-white hover:bg-[#BA1826] rounded-lg px-6 py-3 font-semibold"
            >
              Book Ambulance
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
